import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface MappingPricingProps {
  isPublic?: boolean;
}

// Graduated pricing tiers (must match backend)
const CANDIDATE_TIERS = [
  { min: 0, max: 2499, rate: 0.200, label: 'First 2,499' },
  { min: 2500, max: 4999, rate: 0.175, label: '2,500 – 4,999' },
  { min: 5000, max: 7499, rate: 0.150, label: '5,000 – 7,499' },
  { min: 7500, max: 9999, rate: 0.125, label: '7,500 – 9,999' },
  { min: 10000, max: 14999, rate: 0.100, label: '10,000 – 14,999' },
  { min: 15000, max: 24999, rate: 0.080, label: '15,000 – 24,999' },
  { min: 25000, max: 49999, rate: 0.065, label: '25,000 – 49,999' },
  { min: 50000, max: 99999, rate: 0.055, label: '50,000 – 99,999' },
  { min: 100000, max: 149999, rate: 0.045, label: '100,000 – 149,999' },
  { min: 150000, max: 249999, rate: 0.040, label: '150,000 – 249,999' },
];

const COMPANY_TIERS = [
  { min: 0, max: 2499, rate: 0.100 },
  { min: 2500, max: 4999, rate: 0.090 },
  { min: 5000, max: 7499, rate: 0.080 },
  { min: 7500, max: 9999, rate: 0.070 },
  { min: 10000, max: 14999, rate: 0.060 },
  { min: 15000, max: 24999, rate: 0.055 },
  { min: 25000, max: 49999, rate: 0.050 },
  { min: 50000, max: 99999, rate: 0.045 },
  { min: 100000, max: 149999, rate: 0.040 },
  { min: 150000, max: 249999, rate: 0.040 },
];

interface BreakdownItem {
  bracket: string;
  rate: number | null;
  quantity: number;
  subtotal: number | null;
  customQuote?: boolean;
}

function calculateGraduated(
  tiers: { min: number; max: number; rate: number }[],
  previousVolume: number,
  newQuantity: number
): { total: number; breakdown: BreakdownItem[]; requiresCustomQuote: boolean } {
  if (newQuantity <= 0) return { total: 0, breakdown: [], requiresCustomQuote: false };

  const breakdown: BreakdownItem[] = [];
  let remaining = newQuantity;
  let position = previousVolume;
  let total = 0;
  let requiresCustomQuote = false;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    if (position > tier.max) continue;

    const startInTier = Math.max(position, tier.min);
    const endInTier = Math.min(startInTier + remaining - 1, tier.max);
    const countInTier = endInTier - startInTier + 1;
    if (countInTier <= 0) continue;

    const tierTotal = countInTier * tier.rate;
    total += tierTotal;
    breakdown.push({
      bracket: `${tier.min.toLocaleString()} – ${tier.max.toLocaleString()}`,
      rate: tier.rate,
      quantity: countInTier,
      subtotal: Math.round(tierTotal * 100) / 100,
    });

    remaining -= countInTier;
    position = endInTier + 1;
  }

  if (remaining > 0) {
    requiresCustomQuote = true;
    breakdown.push({
      bracket: '250,000+',
      rate: null,
      quantity: remaining,
      subtotal: null,
      customQuote: true,
    });
  }

  return { total: Math.round(total * 100) / 100, breakdown, requiresCustomQuote };
}

const MappingPricing: React.FC<MappingPricingProps> = ({ isPublic = false }) => {
  const auth = useAuth();
  const token = isPublic ? null : auth?.token;
  const [lifetimeVolume, setLifetimeVolume] = useState<{ candidates: number; companies: number } | null>(null);
  const [loadingVolume, setLoadingVolume] = useState(!isPublic);

  // Calculator inputs
  const [alreadyMapped, setAlreadyMapped] = useState<string>('');
  const [newCandidates, setNewCandidates] = useState<string>('');
  const [newCompanies, setNewCompanies] = useState<string>('');
  const [companiesManuallySet, setCompaniesManuallySet] = useState(false);
  const [useLifetimeVolume, setUseLifetimeVolume] = useState(true);

  const COMPANY_RATIO = 0.35; // ~35% of candidates based on historical data

  // Fetch lifetime volume on mount (authenticated users only)
  useEffect(() => {
    if (!token || isPublic) { setLoadingVolume(false); return; }
    fetch('/api/pricing/volume', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setLifetimeVolume(data);
          setAlreadyMapped(String(data.candidates));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingVolume(false));
  }, [token, isPublic]);

  // When toggling between lifetime and manual
  useEffect(() => {
    if (useLifetimeVolume && lifetimeVolume) {
      setAlreadyMapped(String(lifetimeVolume.candidates));
    }
  }, [useLifetimeVolume, lifetimeVolume]);

  // Auto-estimate companies from candidates (unless user has manually overridden)
  useEffect(() => {
    if (companiesManuallySet) return;
    const cands = parseInt(newCandidates) || 0;
    if (cands > 0) {
      setNewCompanies(String(Math.round(cands * COMPANY_RATIO)));
    } else {
      setNewCompanies('');
    }
  }, [newCandidates, companiesManuallySet]);

  const prevVol = parseInt(alreadyMapped) || 0;
  const newCand = parseInt(newCandidates) || 0;
  const newComp = parseInt(newCompanies) || 0;

  const candidateCalc = calculateGraduated(CANDIDATE_TIERS, prevVol, newCand);
  const companyCalc = calculateGraduated(COMPANY_TIERS, prevVol, newComp);
  const orderTotal = Math.round((candidateCalc.total + companyCalc.total) * 100) / 100;
  const requiresCustomQuote = candidateCalc.requiresCustomQuote || companyCalc.requiresCustomQuote;
  const belowMinimum = orderTotal > 0 && orderTotal < 500 && !requiresCustomQuote;

  const formatCurrency = (amount: number) => {
    return `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Find current tier for highlight
  const getCurrentTierIndex = (volume: number) => {
    return CANDIDATE_TIERS.findIndex(t => volume >= t.min && volume <= t.max);
  };
  const currentTierIdx = getCurrentTierIndex(prevVol);

  // Build a map of tier index → candidates in that tier for the current order
  const tierCandidateCounts: Record<number, number> = {};
  if (candidateCalc.breakdown.length > 0) {
    let position = prevVol;
    let remaining = newCand;
    for (let i = 0; i < CANDIDATE_TIERS.length && remaining > 0; i++) {
      const tier = CANDIDATE_TIERS[i];
      if (position > tier.max) continue;
      const startInTier = Math.max(position, tier.min);
      const endInTier = Math.min(startInTier + remaining - 1, tier.max);
      const countInTier = endInTier - startInTier + 1;
      if (countInTier > 0) {
        tierCandidateCounts[i] = countInTier;
        remaining -= countInTier;
        position = endInTier + 1;
      }
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Mapping Pricing</h1>
        <p className="text-gray-500 mt-1">Graduated volume pricing for done-for-you market mapping</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column: Pricing table */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pricing rates card */}
          <div className="card overflow-hidden shadow-card">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Data mapping rates</h2>
              <p className="text-sm text-gray-500 mt-1">
                Each record is priced at the rate for the bracket it falls in — the more you map, the cheaper each additional record becomes. Your volume is cumulative across all projects.
              </p>
            </div>

            <table className="w-full">
              <thead>
                <tr className="bg-[#2c5282] text-white">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Volume Bracket</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider">Per Candidate</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider">Per Company</th>
                </tr>
              </thead>
              <tbody>
                {CANDIDATE_TIERS.map((tier, i) => {
                  const isCurrentTier = i === currentTierIdx;
                  const countInTier = tierCandidateCounts[i] || 0;
                  const isActiveTier = countInTier > 0;
                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-50 transition-colors duration-200 ${
                        isActiveTier
                          ? 'bg-blue-50/60'
                          : isCurrentTier
                          ? 'bg-blue-50/30'
                          : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                      }`}
                    >
                      <td className={`px-6 py-3 text-sm text-gray-700 ${isActiveTier ? 'border-l-3 border-l-blue-500' : ''}`}>
                        <div className="flex items-center gap-2">
                          {tier.label}
                          {isActiveTier && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">
                              {countInTier.toLocaleString()} candidates
                            </span>
                          )}
                          {!isPublic && isCurrentTier && !isActiveTier && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                              YOUR TIER
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`px-6 py-3 text-sm text-right font-mono ${isActiveTier ? 'text-blue-900 font-semibold' : 'text-gray-900'}`}>
                        £{tier.rate.toFixed(3)}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right font-mono ${isActiveTier ? 'text-blue-900 font-semibold' : 'text-gray-900'}`}>
                        £{COMPANY_TIERS[i].rate.toFixed(3)}
                      </td>
                    </tr>
                  );
                })}
                {/* Custom quote row */}
                <tr className="bg-gray-50/50">
                  <td className="px-6 py-3 text-sm text-gray-400 italic">250,000+</td>
                  <td colSpan={2} className="px-6 py-3 text-sm text-gray-400 italic text-right">
                    Get in touch for a custom quote
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Info cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200/80 rounded-xl px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-900">Cumulative volume</p>
                  <p className="text-sm text-green-700 mt-0.5">
                    Your total mapped records carry across every project. Start a second mapping and you pick up exactly where you left off — you never reset to the top tier.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200/80 rounded-xl px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-900">Hiring managers</p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    Found for free with every mapping project. Minimum commitment per order is £500.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gray-50 border border-gray-200/80 rounded-xl px-6 py-5">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-800">Example:</span>{' '}
              A client maps 8,000 candidates (order 1), then returns for 15,000 more (order 2).
              Order 2 picks up at position 8,001 — so the first 1,999 are at £0.125, the next 5,000 at £0.10,
              then 5,000 at £0.08, and the last 3,001 at £0.065. Total for order 2: <strong>£1,344.95</strong>.
            </p>
          </div>
        </div>

        {/* Right column: Calculator */}
        <div className="space-y-6">
          {/* Instant quote calculator */}
          <div className="card overflow-hidden shadow-card sticky top-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Price calculator</h2>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Already mapped */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Already Mapped
                </label>
                <input
                  type="number"
                  min="0"
                  value={alreadyMapped}
                  onChange={e => {
                    setAlreadyMapped(e.target.value);
                    setUseLifetimeVolume(false);
                  }}
                  placeholder="0"
                  className="input bg-gray-100 text-sm py-2.5"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-gray-400">Previous candidate volume</span>
                  {!isPublic && lifetimeVolume && !loadingVolume && (
                    <button
                      onClick={() => setUseLifetimeVolume(true)}
                      className={`text-xs ${useLifetimeVolume ? 'text-blue-600 font-medium' : 'text-gray-400 hover:text-blue-500'}`}
                    >
                      {useLifetimeVolume ? 'Using your data' : 'Use my volume'}
                    </button>
                  )}
                </div>
              </div>

              {/* New candidates + companies */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    New Candidates
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={newCandidates}
                    onChange={e => {
                      setNewCandidates(e.target.value);
                      setCompaniesManuallySet(false);
                    }}
                    placeholder="0"
                    className="input bg-white text-sm py-2.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    New Companies
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={newCompanies}
                    onChange={e => {
                      setNewCompanies(e.target.value);
                      setCompaniesManuallySet(true);
                    }}
                    placeholder="0"
                    className={`input text-sm py-2.5 ${
                      !companiesManuallySet && newComp > 0 ? 'bg-blue-50' : 'bg-white'
                    }`}
                  />
                  {!companiesManuallySet && newComp > 0 && (
                    <span className="text-[10px] text-blue-500 mt-1 block">Est. ~35% of candidates</span>
                  )}
                </div>
              </div>

              {/* Order total */}
              {(newCand > 0 || newComp > 0) && (
                <>
                  <div className={`rounded-xl px-5 py-4 ${
                    requiresCustomQuote
                      ? 'bg-amber-50 border border-amber-200'
                      : belowMinimum
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        requiresCustomQuote ? 'text-amber-700' : belowMinimum ? 'text-red-700' : 'text-green-700'
                      }`}>
                        This order total
                      </span>
                      <span className={`text-2xl font-bold ${
                        requiresCustomQuote ? 'text-amber-900' : belowMinimum ? 'text-red-900' : 'text-green-900'
                      }`}>
                        {requiresCustomQuote ? 'Custom quote' : formatCurrency(orderTotal)}
                      </span>
                    </div>
                    {belowMinimum && (
                      <p className="text-xs text-red-600 mt-2">
                        Minimum commitment is £500. Increase your order or get in touch.
                      </p>
                    )}
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3">
                    {newCand > 0 && candidateCalc.breakdown.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Candidates:</p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {candidateCalc.breakdown.map((b, i) => (
                            <span key={i}>
                              {b.customQuote
                                ? `${b.quantity.toLocaleString()} × custom rate`
                                : `${b.quantity.toLocaleString()} × £${b.rate!.toFixed(3)}`
                              }
                              {i < candidateCalc.breakdown.length - 1 ? ' + ' : ''}
                            </span>
                          ))}
                          {!candidateCalc.requiresCustomQuote && (
                            <span className="font-medium"> = {formatCurrency(candidateCalc.total)}</span>
                          )}
                        </p>
                      </div>
                    )}
                    {newComp > 0 && companyCalc.breakdown.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Companies:</p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {companyCalc.breakdown.map((b, i) => (
                            <span key={i}>
                              {b.customQuote
                                ? `${b.quantity.toLocaleString()} × custom rate`
                                : `${b.quantity.toLocaleString()} × £${b.rate!.toFixed(3)}`
                              }
                              {i < companyCalc.breakdown.length - 1 ? ' + ' : ''}
                            </span>
                          ))}
                          {!companyCalc.requiresCustomQuote && (
                            <span className="font-medium"> = {formatCurrency(companyCalc.total)}</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Lifetime stats (authenticated users only) */}
              {!isPublic && lifetimeVolume && !loadingVolume && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Your lifetime volume</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="stat-card bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                      <p className="text-lg font-bold text-gray-900">{lifetimeVolume.candidates.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Candidates</p>
                    </div>
                    <div className="stat-card bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                      <p className="text-lg font-bold text-gray-900">{lifetimeVolume.companies.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Companies</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact enrichment card (public page only) */}
          {isPublic && (
            <div className="card overflow-hidden shadow-card">
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="section-label">Contact Enrichment</p>
              </div>
              <div className="px-6 py-4">
                <h3 className="text-base font-semibold text-gray-900 tracking-tight">Contact data packages</h3>
                <p className="text-sm text-gray-500 mt-0.5">Pay-per-success enrichment credits</p>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  {[
                    { label: 'Work email', credits: '1 cr' },
                    { label: 'Personal email', credits: '3 cr' },
                    { label: 'Mobile number', credits: '10 cr' },
                    { label: 'Only on success', credits: null },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{item.label}{item.credits ? `: ${item.credits}` : ''}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 space-y-2">
                  {[
                    { credits: '5,000', price: '£299', per: '£0.060/cr' },
                    { credits: '10,000', price: '£589', per: '£0.059/cr' },
                    { credits: '15,000', price: '£859', per: '£0.057/cr' },
                    { credits: '25,000', price: '£1,379', per: '£0.055/cr' },
                    { credits: '50,000', price: '£2,339', per: '£0.047/cr' },
                    { credits: '100,000', price: '£4,299', per: '£0.043/cr' },
                  ].map((pkg, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-700">{pkg.credits} credits</span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-gray-900">{pkg.price}</span>
                        <span className="text-[10px] text-gray-400">{pkg.per}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Public wrapper — standalone layout with no sidebar/auth
export const PublicMappingPricing: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header */}
      <div className="bg-white border-b border-gray-200/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-gray-900 tracking-tight">Marketmapr</span>
          </div>
          <a
            href="/auth"
            className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            Sign in
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-10">
        <MappingPricing isPublic />
      </div>
    </div>
  );
};

export default MappingPricing;
